import { Button, Position, Tooltip } from '@blueprintjs/core';
import React, { CSSProperties } from 'react';
import {
  blackpoint,
  blackpointEconomic,
  blackpointHealthy,
  blackpointSpecial,
  blackpointInactive,
  candidates,
  dutyDay,
  dutyDayNight,
  dutyNight,
  iconE,
  search
} from '../../../../assets';
import { TLCode } from '../../../../common/enums/TLCode';
import { MultiCalendarSelectionType } from '../MultiCalendar';
import TranslatedMessage from '../../translation/TranslatedMessage';
import { WithTranslateProps } from '../../WithTranslate';
import { IconNames } from '@blueprintjs/icons';
import { DivLineWithButtonMargin, DivColumn, imageButtonStyle, Title } from '../../styledComponents/StyledComponents';

interface ToolSelectionTypeButtonProps {
  content: string;
  currentSelectionType: MultiCalendarSelectionType | undefined;
  buttonType: MultiCalendarSelectionType;
  onButtonClicked: () => void;
  imgSrc: any;
  isDisabled?: boolean;
  id?: string;
  loading?: boolean;
}

interface ToolboxToolPartProps extends WithTranslateProps {
  selectedType: MultiCalendarSelectionType | undefined;
  onTypeClicked: (type: MultiCalendarSelectionType) => void;
  blackPointHolidayCount?: number;
  blackPointHolidayMax?: number;
  isSecretariat?: boolean;
  style?: CSSProperties | undefined;
  hideTitle?: boolean | undefined;
  isVertical?: boolean | undefined;
  withLable?: boolean | undefined;
  id?: string;
  onTransfertDuty?: () => void;
  loading?: boolean;
}

const ToolSelectionTypeButton = ({
  content,
  currentSelectionType,
  buttonType,
  onButtonClicked,
  imgSrc,
  isDisabled,
  id,
  loading
}: ToolSelectionTypeButtonProps): JSX.Element => {
  const button = (
    <Button
      className={imageButtonStyle.toString()}
      active={currentSelectionType === buttonType}
      onClick={onButtonClicked}
      disabled={isDisabled}
      loading={loading}
      style={
        currentSelectionType === buttonType
          ? {
              borderBottomStyle: 'solid',
              borderBottomColor: 'red',
            }
          : undefined
      }
    >
      <img src={imgSrc} alt={id} />
    </Button>
  );
  return (
    <Tooltip content={content} position={Position.BOTTOM}>
      {button}
    </Tooltip>
  );
};

export const ToolboxToolParts = (props: ToolboxToolPartProps) => {
  return (
    <DivColumn style={props.style}>
      {props.hideTitle !== undefined && props.hideTitle ? (
        <></>
      ) : (
        <DivLineWithButtonMargin>
          <Title>
            <TranslatedMessage id={TLCode.MULTI_CALENDAR_Toolbox} />
          </Title>
        </DivLineWithButtonMargin>
      )}
      <DivLineWithButtonMargin>
        <ToolSelectionTypeButton
          id={'bpHoliday2'}
          content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointHoliday)}
          currentSelectionType={props.selectedType}
          buttonType={MultiCalendarSelectionType.blackpoint}
          onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.blackpoint)}
          imgSrc={blackpoint}
          isDisabled={
            props.blackPointHolidayMax != null &&
            props.blackPointHolidayCount != null &&
            props.blackPointHolidayMax - props.blackPointHolidayCount <= 0
          }
        />
        <ToolSelectionTypeButton
          id={'bpEconomic2'}
          content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointEconomic)}
          currentSelectionType={props.selectedType}
          buttonType={MultiCalendarSelectionType.blackpointEconomic}
          onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.blackpointEconomic)}
          imgSrc={blackpointEconomic}
        />
        <ToolSelectionTypeButton
          id={'bpHealth2'}
          content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointSick)}
          currentSelectionType={props.selectedType}
          buttonType={MultiCalendarSelectionType.blackpointHealthy}
          onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.blackpointHealthy)}
          imgSrc={blackpointHealthy}
        />
        {props.isSecretariat && (
          <>
            <ToolSelectionTypeButton
              id={'dayDuty2'}
              content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDay)}
              currentSelectionType={props.selectedType}
              buttonType={MultiCalendarSelectionType.dayDuty}
              onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.dayDuty)}
              imgSrc={dutyDay}
              isDisabled={!props.isSecretariat}
            />
            <ToolSelectionTypeButton
              id={'dayNightDuty2'}
              content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDayNight)}
              currentSelectionType={props.selectedType}
              buttonType={MultiCalendarSelectionType.dayNightDuty}
              onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.dayNightDuty)}
              imgSrc={dutyDayNight}
              isDisabled={!props.isSecretariat}
            />
            <ToolSelectionTypeButton
              id={'nightDuty2'}
              content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyNight)}
              currentSelectionType={props.selectedType}
              buttonType={MultiCalendarSelectionType.nightDuty}
              onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.nightDuty)}
              imgSrc={dutyNight}
              isDisabled={!props.isSecretariat}
            />
            <ToolSelectionTypeButton
              id={'bpSpecial2'}
              content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_BlackPointSpecial)}
              currentSelectionType={props.selectedType}
              buttonType={MultiCalendarSelectionType.blackpointSpecial}
              onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.blackpointSpecial)}
              imgSrc={blackpointSpecial}
            />
            <ToolSelectionTypeButton
              id={'candidates'}
              content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_Candidates)}
              currentSelectionType={props.selectedType}
              buttonType={MultiCalendarSelectionType.candidates}
              onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.candidates)}
              imgSrc={search}
              loading={false}
            />
          </>
        )}
        <ToolSelectionTypeButton
          id={'delete2'}
          content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_Erase)}
          currentSelectionType={props.selectedType}
          buttonType={MultiCalendarSelectionType.delete}
          onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.delete)}
          imgSrc={iconE}
        />
        {props.isSecretariat && (
          <ToolSelectionTypeButton
            id={'deactivate2'}
            content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_Inactive)}
            currentSelectionType={props.selectedType}
            buttonType={MultiCalendarSelectionType.inactive}
            onButtonClicked={() => props.onTypeClicked(MultiCalendarSelectionType.inactive)}
            imgSrc={blackpointInactive}
          />
        )}
        {props.isSecretariat && props.onTransfertDuty && (
          <Tooltip content={props.translate(TLCode.MULTI_CALENDAR_TOOLBOX_Transfert)} position={Position.BOTTOM}>
            <Button className={imageButtonStyle.toString()} onClick={props.onTransfertDuty} icon={IconNames.SEND_TO} />
          </Tooltip>
        )}
      </DivLineWithButtonMargin>
    </DivColumn>
  );
};
